import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Box, Button, Container, Typography, Chip } from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import { useSelection } from '../../hooks/use-selection';
import { GenericTable, Column } from '../../components/generic/generic.table';
import EditLocation from '../../components/generic/edit.location';
import DeliverySettings from '../../components/generic/delivery.settings';
import { useShopDetails } from '../../hooks/use-shop';
import { DATE_FORMAT } from '../../constants';
import { LIST_ALL_SHOPS, DELETE_LOCATION, DELIVERY_LOCATIONS_FOR_SHOP } from '../../gql/queries';

interface Location {
	_id: string;
	name: string;
	address: {
		city: string;
		street?: string;
		state?: string;
		zipcode?: string;
	};
	email: string;
	phone: string;
	createdAt?: string;
}

/**
 * How the location gets paid, at a glance. Red only when it matters: cash off
 * and online payment not working means customers cannot order at all.
 */
function PaymentChip({ acceptsCash, payment }: { acceptsCash: boolean; payment?: string | null }) {
	const online = payment === 'ACTIVE';
	const lapsed = payment === 'EXPIRED' || payment === 'REVOKED';
	if (!acceptsCash && !online) {
		return <Chip size="small" color="error" label="Can't take orders" />;
	}
	if (lapsed) {
		return <Chip size="small" color="warning" label="Reconnect Razorpay" />;
	}
	if (online) {
		return <Chip size="small" color="success" variant="outlined" label={acceptsCash ? 'Cash + online' : 'Online only'} />;
	}
	return <Chip size="small" variant="outlined" label="Cash only" />;
}

function LocationList() {
	const { shopId } = useShopDetails();
	const { data, loading, refetch } = useQuery(LIST_ALL_SHOPS);
	const [deleteLocation] = useMutation(DELETE_LOCATION);

	/** Which location's delivery settings are open, if any. */
	const [configuring, setConfiguring] = useState<Location | null>(null);

	// One query for every location's delivery state, so the column can say
	// which are live without a request per row.
	const { data: deliveryData, refetch: refetchDelivery } = useQuery(DELIVERY_LOCATIONS_FOR_SHOP, {
		variables: { shopId },
		skip: !shopId,
		fetchPolicy: 'cache-and-network'
	});

	const deliveryByLocation = useMemo(() => {
		const map = new Map<
			string,
			{ isDeliveryEnabled: boolean; deliveryRadiusKm: number; acceptsCash: boolean; payment?: string | null }
		>();
		for (const config of deliveryData?.deliveryLocationsForShop ?? []) {
			map.set(config.locationId, {
				isDeliveryEnabled: config.isDeliveryEnabled,
				deliveryRadiusKm: config.deliveryRadiusKm,
				acceptsCash: config.acceptsCashOnDelivery ?? true,
				payment: config.paymentConnection?.status ?? null
			});
		}
		return map;
	}, [deliveryData]);

	// Razorpay sends the shop admin back here, in the tab it opened, with the
	// outcome in the query string. Say what happened, then tidy the URL so a
	// reload does not say it again.
	const [searchParams, setSearchParams] = useSearchParams();
	useEffect(() => {
		const outcome = searchParams.get('razorpay');
		if (!outcome) return;
		if (outcome === 'connected') {
			toast.success('Razorpay connected. Customers can now pay online at this location.');
			void refetchDelivery();
		} else {
			toast.error(`Razorpay was not connected: ${searchParams.get('reason') ?? 'please try again.'}`);
		}
		const next = new URLSearchParams(searchParams);
		next.delete('razorpay');
		next.delete('reason');
		next.delete('locationId');
		setSearchParams(next, { replace: true });
	}, [refetchDelivery, searchParams, setSearchParams]);

	const columns: Column[] = useMemo(
		() => [
			{ title: 'Location Name', key: 'name' },
			{
				title: 'City',
				key: 'address',
				formatter: (column: Column, row: Location) => row.address?.city || 'N/A'
			},
			{
				title: 'Address',
				key: 'address',
				formatter: (column: Column, row: Location) => {
					const addr = row.address;
					return addr?.street ? `${addr.street}, ${addr.state || ''} ${addr.zipcode || ''}`.trim() : 'N/A';
				}
			},
			{ title: 'Phone', key: 'phone' },
			{ title: 'Email', key: 'email' },
			{
				title: 'Delivery',
				key: '_id',
				formatter: (column: Column, row: Location) => {
					const config = deliveryByLocation.get(row._id);
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							{config ? (
								<Chip
									size="small"
									color={config.isDeliveryEnabled ? 'success' : 'default'}
									label={
										config.isDeliveryEnabled ? `On · ${config.deliveryRadiusKm} km` : 'Paused'
									}
								/>
							) : (
								<Chip size="small" variant="outlined" label="Not set up" />
							)}
							{config ? <PaymentChip acceptsCash={config.acceptsCash} payment={config.payment} /> : null}
							<Button size="small" onClick={() => setConfiguring(row)}>
								{config ? 'Edit' : 'Set up'}
							</Button>
						</Box>
					);
				}
			}
		],
		[deliveryByLocation]
	);

	// Get current shop and its locations
	const currentShop = data?.shops?.find((shop: any) => shop._id === shopId);
	const locations = currentShop?.location || [];

	const locationsSelection = useSelection(locations);

	const handleDeleteOne = useCallback(
		async (location: Location) => {
			if (!window.confirm(`Are you sure you want to delete "${location.name}"? This action cannot be undone.`)) {
				return;
			}

			try {
				await deleteLocation({ 
					variables: { 
						shopId: shopId, 
						locationId: location._id 
					} 
				});
				toast.success(`Deleted ${location.name} successfully.`);
				await refetch();
			} catch (error: any) {
				toast.error(`Failed to delete location: ${error.message}`);
			}
		},
		[deleteLocation, shopId, refetch]
	);

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				py: 8
			}}
		>
			<Container maxWidth="xl">
				<Box sx={{ mb: 3 }}>
					<Typography variant="h4" sx={{ mb: 1 }}>
						Location Management
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="body2" color="text.secondary">
							Manage locations for:
						</Typography>
						<Chip 
							label={currentShop?.name || 'Unknown Shop'} 
							color="primary" 
							size="small" 
						/>
					</Box>
				</Box>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					Add, edit, or remove locations for your shop. Each location can have its own settings and staff.
				</Typography>
				
				<GenericTable
					title='Locations'
					data={locations}
					columns={columns}
					onDeselectAll={locationsSelection.handleDeselectAll}
					onDeselectOne={locationsSelection.handleDeselectOne}
					onSelectAll={locationsSelection.handleSelectAll}
					onSelectOne={locationsSelection.handleSelectOne}
					onDeleteOne={handleDeleteOne}
					selected={locationsSelection.selected}
					addEditComponent={EditLocation}
					showSelectCheckBox={false}
					refetch={refetch}
					loading={loading}
				/>

				{configuring ? (
					<DeliverySettings
						open
						locationId={configuring._id}
						locationName={configuring.name}
						onClose={() => setConfiguring(null)}
						onSaved={() => void refetchDelivery()}
					/>
				) : null}
			</Container>
		</Box>
	);
}

export default LocationList;
